import { FunctionComponent, JSX } from 'preact';
import { useCallback, useEffect } from 'preact/hooks';
import { ProviderAccessMap } from '@vgtv/svp-access/lib/access_mappings';
import type { StreamProvider } from '@vgtv/api-client';

import { useTranslation } from '../../../../utils/useTranslation';
import ns from '../../translations';
import { Button, ButtonProps } from '../Button/Button';
import { getSubscriptionLabel } from '../../../../utils/subscription';
import { usePaywallState } from '../../hooks/usePaywallState';
import { useProvider } from '../../hooks/useProvider';
import { usePlayer } from '../../hooks/PlayerProvider';
import { useSchibstedAccount } from '../../../../utils/SchibstedAccountProvider';
import { useTracker } from '../../hooks/useTracker';
import { useCurrentAsset } from '../../hooks/CurrentAssetProvider';
import { useAccessDefinitions } from '../../hooks/AccessDefinitionsProvider';

interface SubscribeButtonProps extends ButtonProps {
  subscriptionUrl?: string;
  accessLevel: ProviderAccessMap[StreamProvider];
  onSubscribeClick: () => void;
  labelOverride?: string;
}

export const SubscribeButton: FunctionComponent<SubscribeButtonProps> = ({
  subscriptionUrl,
  accessLevel,
  onSubscribeClick,
  labelOverride,
  className,
  ...props
}) => {
  const player = usePlayer();
  const { t } = useTranslation(ns);
  const { configProvider } = useProvider();
  const { canUpgrade, hasAccess } = usePaywallState(accessLevel);
  const { clearSessionCache, clearAccessCache } = useSchibstedAccount();
  const { trackButtonClick, trackView } = useTracker();
  const { asset } = useCurrentAsset();
  const { accessDefinitions } = useAccessDefinitions();

  useEffect(() => {
    trackView({
      elementType: 'PaywallButton',
      name: 'PaywallButton',
    });
  }, [trackView]);

  useEffect(() => {
    if (!hasAccess) {
      clearAccessCache().then(() => {
        clearSessionCache();
      });
    }
  }, [hasAccess, clearSessionCache, clearAccessCache]);

  const label =
    labelOverride ||
    t(
      canUpgrade ? 'Switch to subscription' : 'Buy subscription',
      {
        subscription: getSubscriptionLabel(accessDefinitions, accessLevel),
      },
      { noJSX: true }
    );

  const handleSubscribeClick: JSX.MouseEventHandler<HTMLButtonElement> =
    useCallback(
      (e) => {
        e.preventDefault();
        trackButtonClick(
          {
            elementIdentifier: 'paywall-button',
            elementType: 'PaywallButton',
            title: label,
          },
          {
            '@id': `sdrn:vgtv:salesposter:${asset?.id}:element:subscription-paywall`,
            '@type': 'UIElement',
            'name': 'SubscriptionPaywall',
            'url': window.location.href,
          }
        ).finally(() => onSubscribeClick());

        player.trigger('subscribe', {
          button: {
            elementType: 'SubscribeButton',
            title: label,
          },
          subscription: {
            url: subscriptionUrl,
            provider: configProvider,
            name: accessLevel,
          },
        });
      },
      [
        trackButtonClick,
        label,
        asset?.id,
        player,
        subscriptionUrl,
        configProvider,
        accessLevel,
        onSubscribeClick,
      ]
    );

  return (
    <Button onClick={handleSubscribeClick} {...props} className={className}>
      {label}
    </Button>
  );
};
